import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useReadMessages } from "./useReadMessages";

const UnreadLeadsContext = createContext(null);

// Single source of truth for "leads with unread tradie messages" — used by
// the customer dashboard banner, the customer header burger badge, and the
// tradie header burger badge. Wraps useReadMessages so the underlying
// localStorage-backed read state lives in exactly one React instance, and
// adds a useState-backed version counter so a markLeadRead() in any consumer
// re-renders all consumers (the bare ref-based useReadMessages does not).
export function UnreadLeadsProvider({ children }) {
  const reads = useReadMessages();
  // Snapshot of read lead IDs in useState (not a ref) so a markLeadRead in
  // any consumer re-renders all consumers via context.
  const [readSnapshot, setReadSnapshot] = useState(
    () => new Set(reads.readLeadIds.keys())
  );
  const [registeredLeadIds, setRegisteredLeadIds] = useState(() => new Set());

  const markLeadRead = useCallback(
    (leadId) => {
      if (!leadId) return;
      reads.markAsRead(leadId);
      setReadSnapshot(new Set(reads.readLeadIds.keys()));
    },
    [reads]
  );

  const isLeadRead = useCallback(
    (leadId) => readSnapshot.has(leadId),
    [readSnapshot]
  );

  const registerLeadsWithTradieActivity = useCallback((leadIds) => {
    setRegisteredLeadIds(new Set(leadIds || []));
  }, []);

  const unreadLeadIds = useMemo(() => {
    const out = new Set();
    registeredLeadIds.forEach((id) => {
      if (!readSnapshot.has(id)) out.add(id);
    });
    return out;
  }, [registeredLeadIds, readSnapshot]);

  const value = useMemo(
    () => ({
      unreadLeadIds,
      unreadCount: unreadLeadIds.size,
      markLeadRead,
      isLeadRead,
      registerLeadsWithTradieActivity,
    }),
    [unreadLeadIds, markLeadRead, isLeadRead, registerLeadsWithTradieActivity]
  );

  return (
    <UnreadLeadsContext.Provider value={value}>
      {children}
    </UnreadLeadsContext.Provider>
  );
}

export function useUnreadLeads() {
  const ctx = useContext(UnreadLeadsContext);
  if (!ctx) {
    throw new Error("useUnreadLeads must be used within UnreadLeadsProvider");
  }
  return ctx;
}
